import { useState, useEffect } from "react";
import * as React from "react";
import Toggle from "react-toggle";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import * as XLSX from "xlsx";

import { TIME_RANGES, classNames, niceDate, niceDateTime, niceNumber } from "../../lib/Util";
import { translate } from "../IntlProviderWrapper";
import ErrorComponent from "../ErrorComponent";
import Loader from "../Loader";
import Pagination from "../react/Pagination";
import DateTimePicker from "../DateTimePicker";
import MultiSelect from "../MultiSelect.tsx";
import TranslatedMaterialTitle from "../TranslatedMaterialTitle.tsx";
import FilterSlashIcon from "../react/Icon/FilterSlash";

import type { ErrorType } from "../ErrorComponent";
import type { ILabelValuePair } from "../../lib/Util";
import local_settings, { FIELD_HIDDEN_COLUMNS, FIELD_TIME_WINDOW } from "../../lib/LocalSettings";

import "./LargeDataTable.scss";

type TableRowBase = Record<string, any>;

export type ColumnId<TableRow extends TableRowBase> = Extract<keyof TableRow, string>;

export type TableFooter<TableRow extends TableRowBase> = Record<keyof TableRow, any>;

export type TableColumn<TableRow extends TableRowBase> = {
    id: ColumnId<TableRow>;
    title: string;
    disable_sort?: boolean;
    filter_options?: ILabelValuePair[];
    formatExport?: (props: TableCellProps<TableRow>) => any;
    Header?: React.ComponentType<TableHeaderProps<TableRow>>;
    Cell?: React.ComponentType<TableCellProps<TableRow>>;
    Footer?: React.ComponentType<TableFooterProps<TableRow>>;
};

export type TableHeaderProps<TableRow extends TableRowBase> = {
    column: TableColumn<TableRow>;
    filters: Filters<TableRow>;
    page_index: number;
    page_size: number;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
    table_props: Props<TableRow>;
    clearFilters: (id?: ColumnId<TableRow>[]) => void;
    onFilterChange: (id: ColumnId<TableRow>, value: string, delay?: number) => void;
    onSortChange: (id: ColumnId<TableRow>) => void;
};

export type TableCellProps<TableRow extends TableRowBase> = {
    table_props: Props<TableRow>;
    column: TableColumn<TableRow>;
    row: TableRow;
};

export type TableFooterProps<TableRow extends TableRowBase> = {
    table_props: Props<TableRow>;
    column: TableColumn<TableRow>;
    footer: TableFooter<TableRow>;
    filters: Filters<TableRow>;
    page_index: number;
    page_size: number;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
};

export type Filters<TableRow extends TableRowBase> = Record<ColumnId<TableRow>, string>;

export type TableExportData<TableRow extends TableRowBase> = {
    rows: TableRow[] | null;
    title: string;
    file_name: string;
};

export type Props<TableRow extends TableRowBase> = {
    id: string;
    class_name?: string;
    columns: TableColumn<TableRow>[];
    default_filters: Filters<TableRow>;
    default_page_size: number;
    error: string;
    footer?: TableFooter<TableRow>;
    is_loading: boolean;
    is_updating: boolean;
    page_sizes?: number[];
    rows: TableRow[];
    show_details?: boolean;
    total_count: number;
    always_visible_columns?: ColumnId<TableRow>[];
    loadExportData: (
        export_all: boolean,
        filters: Filters<TableRow>,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ) => Promise<TableExportData<TableRow>>;
    onDataUpdate: (
        filters: Filters<TableRow>,
        page_index: number,
        page_size: number,
        order_by: ColumnId<TableRow>,
        order_desc: boolean
    ) => Promise<void>;
    onTotalCountUpdate?: (filters: Filters<TableRow>) => Promise<void>;
    onFiltersChange?: (filters: Filters<TableRow>) => void;
    onScroll?: (event: React.UIEvent<HTMLElement>) => void;
    onDetailsToggle?: (show: boolean) => void;
};

type State<TableRow extends TableRowBase> = {
    filters: Filters<TableRow>;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
    page_index: number;
    page_size: number;
    show_settings: boolean;
    show_export_config: boolean;
};

const TEXT_FILTER_DELAY = 500;

export const DEFAULT_PAGE_SIZE = 50;

export const DEFAULT_NUMBER_OF_DAYS = 7;

export const getFieldName = (id: string, field: string): string => `${id}.${field}`;

export const loadTimeWindow = (id: string): number => {
    const time_window = local_settings.getItem(getFieldName(id, FIELD_TIME_WINDOW));
    if (time_window !== null) {
        return Number(time_window);
    }

    return DEFAULT_NUMBER_OF_DAYS * TIME_RANGES.DAY;
};

export const storeTimeWindow = (id: string, time_window: number) => {
    local_settings.setItem(getFieldName(id, FIELD_TIME_WINDOW), String(time_window));
};

const loadHiddenColumns = <TableRow extends TableRowBase>(id: string): ColumnId<TableRow>[] => {
    const hidden_columns: ColumnId<TableRow>[] = [];
    const hidden_columns_raw = local_settings.getItem(getFieldName(id, FIELD_HIDDEN_COLUMNS));
    if (hidden_columns_raw != null) {
        try {
            const hidden_columns_parsed = JSON.parse(hidden_columns_raw);
            if (Array.isArray(hidden_columns_parsed)) {
                for (const column of hidden_columns_parsed) {
                    hidden_columns.push(column);
                }
            }
        } catch (error) {
            // parse error
        }
    }

    return hidden_columns;
};

const storeHiddenColumns = <TableRow extends TableRowBase>(id: string, hidden_columns: ColumnId<TableRow>[]) => {
    const field_name = getFieldName(id, FIELD_HIDDEN_COLUMNS);
    if (hidden_columns.length > 0) {
        local_settings.setItem(field_name, JSON.stringify(hidden_columns));
    } else {
        local_settings.removeItem(field_name);
    }
};

export class FilterParser<TableRow extends TableRowBase> {
    filters: Filters<TableRow> = {} as Filters<TableRow>;

    constructor(filters: Filters<TableRow>) {
        this.filters = filters;
    }

    get(key: ColumnId<TableRow>): string | undefined {
        const value = this.filters[key];
        return value;
    }

    getBoolean = (key: ColumnId<TableRow>): boolean | undefined => {
        const value = this.get(key);
        if (value === "true") {
            return true;
        }

        if (value === "false") {
            return false;
        }

        return undefined;
    };

    getTimestamp(key: ColumnId<TableRow>): number | undefined {
        const value = this.get(key);
        if (value !== undefined) {
            const timestamp = new Date(value).getTime();
            if (!Number.isNaN(timestamp)) {
                return timestamp;
            }
        }

        return undefined;
    }

    getOptions = (key: ColumnId<TableRow>): string[] => {
        const filter = this.get(key);
        if (filter) {
            return filter.split(",");
        }

        return [];
    };
}

export const booleanToYesNo = (value: boolean): string =>
    value ? translate("common.yes", "Yes") : translate("common.no", "No");

export const getFieldNameMin = <TableRow extends TableRowBase>(name: ColumnId<TableRow>): ColumnId<TableRow> =>
    `${name}_min` as ColumnId<TableRow>;

export const getFieldNameMax = <TableRow extends TableRowBase>(name: ColumnId<TableRow>): ColumnId<TableRow> =>
    `${name}_max` as ColumnId<TableRow>;

export const formatBoolean = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>): string => {
    const { column, row } = props;
    const value: boolean = Boolean(row[column.id]);
    return booleanToYesNo(value);
};

export const formatDate = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>): string => {
    const { column, row } = props;
    const value = new Date(row[column.id]);
    return niceDate(value);
};

export const formatDateTime = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    const { column, row } = props;
    const value = new Date(row[column.id]);
    return niceDateTime(value);
};

export const formatMaterialTitle = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    const { row } = props;
    return TranslatedMaterialTitle({
        material_external_id: row.material_external_id,
        material_title: row.material_title,
    });
};

export const formatMissingValue = (): string => {
    return "N/A";
};

export const StringCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    const { column, row } = props;
    const value = row[column.id];
    return <span>{value}</span>;
};

export const BooleanCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    const { column, row } = props;
    const value = Boolean(row[column.id]);
    return <span>{booleanToYesNo(value)}</span>;
};

export const FloatCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    const { column, row } = props;
    const value = Number(row[column.id]);
    return <span>{niceNumber(value, 2)}</span>;
};

export const DateCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    return <span>{formatDate(props)}</span>;
};

export const DateTimeCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    return <span>{formatDateTime(props)}</span>;
};

export const MaterialTitleCell = <TableRow extends TableRowBase>(props: TableCellProps<TableRow>) => {
    return <span>{formatMaterialTitle(props)}</span>;
};

export const SortButton = <TableRow extends TableRowBase>(props: {
    column: TableColumn<TableRow>;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
    disabled?: boolean;
    onChange: (id: ColumnId<TableRow>) => void;
}) => {
    const { id, disable_sort } = props.column;
    if (disable_sort) {
        return null;
    }

    let title = translate("common.sort_by_default", "Sort by default");
    if (props.order_by !== id) {
        title = translate("common.sort_asc", "Sort ascending");
    } else if (!props.order_desc) {
        title = translate("common.sort_desc", "Sort descending");
    }

    return (
        <button
            className="large-data-table__column-sort btn btn-short btn-flat-light"
            disabled={props.disabled === true}
            title={props.disabled !== true ? title : ""}
            onClick={() => props.onChange(id)}
        >
            <i
                className={classNames("fas", {
                    "fa-sort": props.order_by !== id,
                    "fa-sort-up": props.order_by === id && !props.order_desc,
                    "fa-sort-down": props.order_by === id && props.order_desc,
                })}
            />
        </button>
    );
};

export const TextFilter = <TableRow extends TableRowBase>(props: TableHeaderProps<TableRow>) => {
    const { id, title } = props.column;
    return (
        <div className="large-data-table__text-filter">
            <label>
                <span>{title}</span>
                <input
                    className="form-control column-filter"
                    name={id}
                    value={props.filters[id] !== undefined ? props.filters[id] : ""}
                    onChange={(event: React.SyntheticEvent<HTMLInputElement>) =>
                        props.onFilterChange(id, event.currentTarget.value, TEXT_FILTER_DELAY)
                    }
                    placeholder={title}
                    title={title}
                />
            </label>
            {!props.column.disable_sort && (
                <SortButton
                    column={props.column}
                    order_by={props.order_by}
                    order_desc={props.order_desc}
                    onChange={props.onSortChange}
                    disabled={props.table_props.is_updating}
                />
            )}
        </div>
    );
};

export const TextFilterWithReset = <TableRow extends TableRowBase>(props: TableHeaderProps<TableRow>) => {
    const { id, title } = props.column;
    const has_filter_changed = Object.keys(props.filters).some(
        key =>
            (props.filters[key] !== "" && props.filters[key] !== props.table_props.default_filters[key]) ||
            (props.filters[key] === "" && props.table_props.default_filters[key] !== undefined)
    );
    return (
        <div className="large-data-table__text-filter">
            <label>
                <span>{title}</span>
                <input
                    className="form-control column-filter"
                    name={id}
                    value={props.filters[id] !== undefined ? props.filters[id] : ""}
                    onChange={(event: React.SyntheticEvent<HTMLInputElement>) =>
                        props.onFilterChange(id, event.currentTarget.value, TEXT_FILTER_DELAY)
                    }
                    placeholder={title}
                    title={title}
                />
            </label>
            {!props.column.disable_sort && (
                <SortButton
                    column={props.column}
                    order_by={props.order_by}
                    order_desc={props.order_desc}
                    onChange={props.onSortChange}
                    disabled={props.table_props.is_updating}
                />
            )}
            {has_filter_changed && (
                <button
                    className="btn btn-short btn-outline-secondary d-flex align-items-center justify-content-center ml-2"
                    title={translate("common.reset_filters", "Reset filters")}
                    onClick={() => {
                        props.clearFilters();
                    }}
                    disabled={props.table_props.is_updating}
                >
                    <FilterSlashIcon height="fs" />
                </button>
            )}
        </div>
    );
};

export const DateRangeFilter = <TableRow extends TableRowBase>(props: TableHeaderProps<TableRow>) => {
    const { column } = props;
    const name_min = getFieldNameMin(column.id);
    const title_min = `${column.title} (${translate("common.min", "Min")})`;
    let value_min: Date | undefined = new Date(props.filters[name_min]);
    if (isNaN(value_min.getTime())) {
        value_min = undefined;
    }

    const name_max = getFieldNameMax(column.id);
    const title_max = `${column.title} (${translate("common.max", "Max")})`;
    let value_max: Date | undefined = new Date(props.filters[name_max]);
    if (isNaN(value_max.getTime())) {
        value_max = undefined;
    }

    return (
        <div className="large-data-table__date-range-filter">
            <label>
                <span>{column.title}</span>
                <div>
                    <DateTimePicker
                        name={name_min}
                        value={value_min}
                        placeholder={title_min}
                        title={title_min}
                        maxValue={value_max}
                        onChange={date => props.onFilterChange(name_min, date.toISOString())}
                        onClear={() => props.onFilterChange(name_min, "")}
                        popupParent={document.body}
                    />
                    <DateTimePicker
                        name={name_max}
                        value={value_max}
                        placeholder={title_max}
                        title={title_max}
                        minValue={value_min}
                        onChange={date => props.onFilterChange(name_max, date.toISOString())}
                        onClear={() => props.onFilterChange(name_max, "")}
                        popupParent={document.body}
                    />
                </div>
            </label>
            {!props.column.disable_sort && (
                <SortButton
                    column={props.column}
                    order_by={props.order_by}
                    order_desc={props.order_desc}
                    onChange={props.onSortChange}
                    disabled={props.table_props.is_updating}
                />
            )}
        </div>
    );
};

export const MultiSelectFilter = <TableRow extends TableRowBase>(props: TableHeaderProps<TableRow>) => {
    const { column } = props;
    let options: ILabelValuePair[] = [];
    if (column.filter_options !== undefined) {
        options = column.filter_options;
    }

    const filter = props.filters[column.id] || "";
    const values = filter.length > 0 ? filter.split(",") : [];
    const options_map = new Map(options.map(option => [option.value, option]));
    const selected_options = values.map(value => {
        const option = options_map.get(value);
        const label = option ? option.label : value;
        return { value, label };
    });

    return (
        <div className="large-data-table__multiselect-filter">
            <label title={column.title}>
                <span>{column.title}</span>
                <MultiSelect
                    className="w-100"
                    name={column.id}
                    placeholder={column.title}
                    value={selected_options}
                    options={options}
                    onChange={(selected: ILabelValuePair[]) =>
                        props.onFilterChange(column.id, selected.map(option => String(option.value)).join(","))
                    }
                    isClearable={false}
                    enable_select_all
                />
            </label>
            {!props.column.disable_sort && (
                <SortButton
                    column={props.column}
                    order_by={props.order_by}
                    order_desc={props.order_desc}
                    onChange={props.onSortChange}
                    disabled={props.table_props.is_updating}
                />
            )}
        </div>
    );
};

export const SelectFilter = <TableRow extends TableRowBase>(props: TableHeaderProps<TableRow>) => {
    const { column } = props;
    let options: ILabelValuePair[] = [];
    if (column.filter_options !== undefined) {
        options = column.filter_options;
    }

    const value = props.filters[column.id] || "";
    const selected = options.find(option => option.value === value);
    return (
        <div className="large-data-table__select-filter">
            <label title={column.title}>
                <span>{column.title}</span>
                <Select
                    className="w-100"
                    name={column.id}
                    placeholder={column.title}
                    value={selected}
                    options={options}
                    onChange={(selected: ILabelValuePair) =>
                        props.onFilterChange(column.id, selected != null ? String(selected.value) : "")
                    }
                    isClearable
                />
            </label>
            {!props.column.disable_sort && (
                <SortButton
                    column={props.column}
                    order_by={props.order_by}
                    order_desc={props.order_desc}
                    onChange={props.onSortChange}
                    disabled={props.table_props.is_updating}
                />
            )}
        </div>
    );
};

export const DefaultFiltersForm = <TableRow extends TableRowBase>(props: {
    table_id: string;
    title: string;
    getDefaultFilters: (start_date: Date) => Filters<TableRow>;
    onSubmit: (filters: Filters<TableRow>) => Promise<void>;
}) => {
    const { table_id, title, getDefaultFilters, onSubmit } = props;
    const [time_window, setTimeWindow] = useState<number>(loadTimeWindow(table_id));
    const start_date = new Date(new Date().getTime() - time_window);

    const handleStartDateChange = (date: Date) => {
        const time_diff = new Date().getTime() - date.getTime();
        setTimeWindow(time_diff);
        storeTimeWindow(table_id, time_diff);
    };

    const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit(getDefaultFilters(start_date));
    };

    return (
        <div className="large-data-table__default-filters">
            <div className="card">
                <div className="card-header">
                    <h2 className="h5 mb-0">{title}</h2>
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="default-filters-number-of-weeks">
                                {translate("OrderTableProduction.time_start", "Start time")}
                            </label>
                            <DateTimePicker
                                value={start_date}
                                maxValue={new Date()}
                                onChange={handleStartDateChange}
                                popupParent={document.body}
                            />
                        </div>
                        <div className="text-right">
                            <button type="submit" className="btn btn-primary" autoFocus>
                                {translate("common.search", "Search")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const HeaderCell = <TableRow extends TableRowBase>(props: {
    column: TableColumn<TableRow>;
    filters: Filters<TableRow>;
    page_index: number;
    page_size: number;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
    table_props: Props<TableRow>;
    clearFilters: (id?: ColumnId<TableRow>[]) => void;
    onFilterChange: (id: ColumnId<TableRow>, value: string, delay?: number) => void;
    onSortChange: (id: ColumnId<TableRow>) => void;
}) => {
    const {
        column,
        filters,
        page_index,
        page_size,
        order_by,
        order_desc,
        table_props,
        clearFilters,
        onFilterChange,
        onSortChange,
    } = props;

    const Header = column.Header;
    if (Header !== undefined) {
        return (
            <Header
                column={column}
                filters={filters}
                page_index={page_index}
                page_size={page_size}
                order_by={order_by}
                order_desc={order_desc}
                table_props={table_props}
                clearFilters={clearFilters}
                onFilterChange={onFilterChange}
                onSortChange={onSortChange}
            />
        );
    }

    return (
        <div className="large-data-table__sort">
            <label>
                <span>{column.title}</span>
                <SortButton
                    column={column}
                    order_by={order_by}
                    order_desc={order_desc}
                    onChange={() => onSortChange(column.id)}
                    disabled={props.table_props.is_updating}
                />
            </label>
        </div>
    );
};

const DataCell = <TableRow extends TableRowBase>(props: {
    column: TableColumn<TableRow>;
    row: TableRow;
    table_props: Props<TableRow>;
}) => {
    const { column, row, table_props } = props;
    const value = row[column.id];
    if (value == null) {
        return <span>{formatMissingValue()}</span>;
    }

    const Cell = column.Cell;
    if (Cell !== undefined) {
        return <Cell column={column} row={row} table_props={table_props} />;
    }

    return <span>{value}</span>;
};

const FooterCell = <TableRow extends TableRowBase>(props: {
    column: TableColumn<TableRow>;
    footer: TableFooter<TableRow>;
    table_props: Props<TableRow>;
    filters: Filters<TableRow>;
    page_index: number;
    page_size: number;
    order_by: ColumnId<TableRow>;
    order_desc: boolean;
}) => {
    const { column, footer, table_props, filters, page_index, page_size, order_by, order_desc } = props;
    const value = footer[String(column.id)];
    if (value == null) {
        return null;
    }

    const Footer = column.Footer;
    if (Footer !== undefined) {
        return (
            <Footer
                column={column}
                footer={footer}
                table_props={table_props}
                filters={filters}
                page_index={page_index}
                page_size={page_size}
                order_by={order_by}
                order_desc={order_desc}
            />
        );
    }

    return <span>{value}</span>;
};

const StatusBar = (props: {
    total_count: number;
    is_loading: boolean;
    is_updating: boolean;
    page_index: number;
    page_size: number;
    page_sizes?: number[];
    show_details?: boolean;
    gotoPage: (page_index: number) => void;
    gotoNextPage: () => void;
    gotoPreviousPage: () => void;
    setPageSize: (page_size: number) => void;
    onSettingsOpen: (show?: boolean) => void;
    onExportConfigOpen: () => void;
    onDetailsToggle?: (show: boolean) => void;
}) => {
    const {
        total_count,
        is_loading,
        is_updating,
        page_sizes,
        page_index,
        page_size,
        show_details,
        gotoPage,
        gotoNextPage,
        gotoPreviousPage,
        setPageSize,
        onDetailsToggle,
        onSettingsOpen,
        onExportConfigOpen,
    } = props;
    const page_count = Math.ceil(total_count / page_size);
    const page_options = page_count < 100 ? [...Array(page_count).keys()] : [];
    const is_ready = !is_updating && !is_loading;

    const handleShowDetailsToggle = (event: React.SyntheticEvent<HTMLInputElement>) => {
        if (onDetailsToggle !== undefined) {
            onDetailsToggle(event.currentTarget.checked);
        }
    };

    const handleSettingsOpen = () => {
        onSettingsOpen();
    };

    const handleExportConfigOpen = () => {
        onExportConfigOpen();
    };

    return (
        <div className="large-data-table__statusbar">
            <Pagination
                can_previous_page={is_ready && page_index > 0}
                can_next_page={is_ready && (page_index < page_count - 1 || total_count < 0)}
                num_rows={total_count}
                page_options={page_options}
                page_count={page_count}
                page_index={page_index}
                page_size={page_size}
                page_sizes={page_sizes}
                gotoPage={gotoPage}
                nextPage={gotoNextPage}
                previousPage={gotoPreviousPage}
                setPageSize={setPageSize}
            />
            <div className="large-data-table__status">
                {onDetailsToggle !== undefined && (
                    <label className="large-data-table__details-toggle">
                        <span>{translate("common.details", "Details")}</span>
                        <Toggle checked={show_details} onChange={handleShowDetailsToggle} />
                    </label>
                )}
                <div className="large-data-table__status-buttons">
                    <button
                        type="button"
                        className="btn btn-short btn-outline-secondary"
                        title={translate("common.export_xlsx", "Export to Excel")}
                        onClick={handleExportConfigOpen}
                        disabled={!is_ready}
                    >
                        <i className="fas fa-file-export" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-short btn-outline-secondary"
                        title={translate("Header.menu.settings", "Settings")}
                        onClick={handleSettingsOpen}
                        disabled={show_details}
                    >
                        <i className="fas fa-cog" />
                    </button>
                </div>
                <span
                    className={classNames("large-data-table__status-icon", {
                        "is-ready": is_ready,
                    })}
                >
                    {is_updating || is_loading ? (
                        <i className="fas fa-spinner fa-spin is-loading" />
                    ) : (
                        <i className="ready far fa-check-circle is-ready" />
                    )}
                </span>
            </div>
        </div>
    );
};

const SettingsModal = <TableRow extends TableRowBase>(props: {
    show: boolean;
    columns: TableColumn<TableRow>[];
    table_id: string;
    always_visible_columns?: ColumnId<TableRow>[];
    onClose: () => void;
}) => {
    const { always_visible_columns = [], show, columns, table_id, onClose } = props;
    const [hidden_columns, setHiddenColumns] = useState<ColumnId<TableRow>[]>([]);

    useEffect(() => {
        if (show) {
            setHiddenColumns(loadHiddenColumns<TableRow>(table_id));
        }
    }, [show]);

    const handleColumnToggle = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { value, checked } = event.currentTarget;
        if (!checked) {
            if (!hidden_columns.includes(value as ColumnId<TableRow>)) {
                setHiddenColumns([...hidden_columns, value as ColumnId<TableRow>]);
            }
        } else {
            setHiddenColumns(hidden_columns.filter(column => column !== value));
        }
    };

    const handleSave = () => {
        storeHiddenColumns(table_id, hidden_columns);
        onClose();
    };

    return (
        <Modal
            className="large-data-table__settings-modal"
            dialogClassName="modal-dialog-scrollable"
            show={show}
            onHide={onClose}
        >
            <Modal.Header>
                <Modal.Title>{translate("Header.menu.settings", "Settings")}</Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <form>
                    <fieldset className="form-group form-group-columns">
                        <legend className="h6 pt-0">{translate("common.columns", "Columns")}</legend>
                        {columns.map(column => {
                            const is_hidden = hidden_columns.includes(column.id);
                            return (
                                <div key={column.id} className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name={column.id}
                                        id={column.id}
                                        value={column.id}
                                        checked={!is_hidden}
                                        disabled={always_visible_columns.includes(column.id)}
                                        onChange={handleColumnToggle}
                                    />
                                    <label className="form-check-label" htmlFor={column.id}>
                                        {column.title}
                                    </label>
                                </div>
                            );
                        })}
                    </fieldset>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                    {translate("common.save", "Save")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const ExportConfigModal = <TableRow extends TableRowBase>(props: {
    show: boolean;
    columns: TableColumn<TableRow>[];
    rows: TableRow[];
    total_count: number;
    always_visible_columns?: ColumnId<TableRow>[];
    onClose: () => void;
    onExport: (export_all: boolean) => Promise<boolean>;
    onTotalCountUpdate: () => Promise<void>;
}) => {
    const { show, rows, total_count, onClose, onExport, onTotalCountUpdate } = props;
    const [export_all, setExportAll] = useState<boolean>(false);
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [export_failed, setExportFailed] = useState<boolean>(false);

    useEffect(() => {
        if (show) {
            setExportAll(false);
            setExportFailed(false);
        }
    }, [show]);

    const isExportTooLarge = (): boolean => {
        return export_all && total_count > 50000;
    };

    const getAlertType = (): ErrorType => {
        if (export_failed || isExportTooLarge()) {
            return "error";
        }

        return "info";
    };

    const getAlertMessage = (): string => {
        if (export_failed) {
            return translate("common.export_failed", "Export failed");
        }

        if (isExportTooLarge()) {
            return `${translate("common.too_many_records", "Too many records")}: ${total_count}`;
        }

        return `${translate("common.number_of_rows", "Number of rows")}: ${
            export_all ? (total_count >= 0 ? total_count : translate("common.loading", "Loading...")) : rows.length
        }`;
    };

    const handleExportScopeChange = async (event: React.SyntheticEvent<HTMLInputElement>) => {
        setExportFailed(false);
        const new_export_value = event.currentTarget.value === "all";
        setExportAll(new_export_value);
        if (new_export_value && total_count < 0) {
            setIsLoading(true);
            await onTotalCountUpdate();
            setIsLoading(false);
        }
    };

    const handleExport = async () => {
        setExportFailed(false);
        setIsLoading(true);
        const success = await onExport(export_all);
        setIsLoading(false);

        if (!success) {
            setExportFailed(true);
            return;
        }

        onClose();
    };

    const handleClose = () => {
        if (!is_loading) {
            onClose();
        }
    };

    return (
        <Modal
            className="large-data-table__export-config-modal"
            dialogClassName="modal-dialog-scrollable"
            show={show}
            onHide={handleClose}
        >
            <Modal.Header>
                <Modal.Title>{translate("common.export_xlsx", "Export to Excel")}</Modal.Title>
                <button type="button" className="close" onClick={handleClose} disabled={is_loading}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <form>
                    <div className="form-group form-group-scope">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="export-scope"
                                id="export-scope-current-page"
                                value="current-page"
                                checked={!export_all}
                                disabled={is_loading}
                                onChange={handleExportScopeChange}
                            />
                            <label className="form-check-label" htmlFor="export-scope-current-page">
                                {translate("common.current_page", "Current page")}
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="export-scope"
                                id="export-scope-all"
                                value="all"
                                checked={export_all}
                                disabled={is_loading}
                                onChange={handleExportScopeChange}
                            />
                            <label className="form-check-label" htmlFor="export-scope-all">
                                {translate("common.all", "All")}
                            </label>
                        </div>
                    </div>
                    <ErrorComponent className="mb-0" type={getAlertType()} msg={getAlertMessage()} />
                </form>
            </Modal.Body>
            <Modal.Footer>
                {is_loading && <i className="fas fa-spinner fa-spin mr-auto" aria-hidden="true" />}
                <button className="btn btn-outline-secondary" type="button" disabled={is_loading} onClick={handleClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleExport}
                    disabled={is_loading || isExportTooLarge()}
                    autoFocus
                >
                    {export_failed ? translate("common.retry", "Retry") : translate("common.export", "Export")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

class LargeDataTable<TableRow extends TableRowBase> extends React.Component<Props<TableRow>, State<TableRow>> {
    filter_timeout: ReturnType<typeof setTimeout> | undefined;

    constructor(props: Props<TableRow>) {
        super(props);
        this.state = {
            filters: { ...props.default_filters },
            order_by: "" as ColumnId<TableRow>,
            order_desc: false,
            page_index: 0,
            page_size: props.default_page_size,
            show_settings: false,
            show_export_config: false,
        };
    }

    componentDidUpdate(prev_props: Props<TableRow>) {
        if (prev_props.total_count < 0 && this.props.total_count >= 0 && this.state.page_index < 0) {
            this.setState({
                page_index: Math.floor(this.props.total_count / this.state.page_size),
            });
        }
    }

    getExportData = async (export_all: boolean): Promise<{ data: any[][] | null; title: any; file_name: string }> => {
        const { columns, footer, id, show_details } = this.props;
        const { filters, order_by, order_desc } = this.state;
        const hidden_columns = new Set(loadHiddenColumns<TableRow>(id));
        const { rows, title, file_name } = await this.props.loadExportData(export_all, filters, order_by, order_desc);
        let data: any[][] | null = null;

        if (rows !== null) {
            data = [];

            // Header
            const header_row: string[] = [];
            for (const column of columns) {
                if (hidden_columns.has(column.id) && !show_details) {
                    continue;
                }

                header_row.push(column.title);
            }

            data.push(header_row);

            // Rows
            for (const row of rows) {
                const data_row: string[] = [];
                for (const column of columns) {
                    if (hidden_columns.has(column.id) && !show_details) {
                        continue;
                    }

                    const value = row[column.id];
                    if (value == null) {
                        data_row.push(formatMissingValue());
                        continue;
                    }

                    const cell_props: TableCellProps<TableRow> = {
                        table_props: this.props,
                        column,
                        row,
                    };
                    data_row.push(column.formatExport !== undefined ? column.formatExport(cell_props) : value);
                }

                data.push(data_row);
            }

            // Footer
            if (footer !== undefined && rows.length !== 0) {
                const footer_row: string[] = [];
                for (const column of columns) {
                    if (hidden_columns.has(column.id) && !show_details) {
                        continue;
                    }

                    const value = footer[column.id];
                    const cell_props: TableCellProps<TableRow> = {
                        table_props: this.props,
                        column,
                        row: footer as TableRow,
                    };
                    footer_row.push(
                        column.formatExport !== undefined && value != null ? column.formatExport(cell_props) : value
                    );
                }

                data.push(footer_row);
            }
        }

        return { data, title, file_name };
    };

    gotoPage = (page_index: number) => {
        this.setState({ page_index }, async () => {
            const { filters, page_index, page_size, order_by, order_desc } = this.state;
            await this.props.onDataUpdate(filters, page_index, page_size, order_by, order_desc);
        });
    };

    gotoNextPage = () => {
        const { total_count } = this.props;
        const { page_index, page_size } = this.state;
        const page_count = Math.ceil(total_count / page_size);
        if (page_index < page_count - 1 || total_count < 0) {
            this.gotoPage(page_index + 1);
        }
    };

    gotoPreviousPage = () => {
        const { page_index } = this.state;
        if (page_index > 0) {
            this.gotoPage(page_index - 1);
        }
    };

    setPageSize = (page_size: number) => {
        this.setState({ page_size, page_index: 0 }, async () => {
            const { filters, order_by, order_desc } = this.state;
            await this.props.onDataUpdate(filters, 0, page_size, order_by, order_desc);
        });
    };

    clearFilters = (ids?: ColumnId<TableRow>[]) => {
        let new_filters: Filters<TableRow> = { ...this.props.default_filters };
        if (ids !== undefined) {
            new_filters = { ...this.state.filters };
            for (const id of ids) {
                if (this.props.default_filters[id] !== undefined) {
                    new_filters[id] = this.props.default_filters[id];
                } else {
                    delete new_filters[id];
                }
            }
        }

        this.setState({ filters: new_filters, page_index: 0 }, async () => {
            const { filters, page_size, order_by, order_desc } = this.state;
            await this.props.onDataUpdate(filters, 0, page_size, order_by, order_desc);
        });
        if (this.props.onFiltersChange !== undefined) {
            this.props.onFiltersChange(new_filters);
        }
    };

    handleSortChange = (id: ColumnId<TableRow>) => {
        if (this.state.order_by !== id) {
            this.setState({ order_by: id, order_desc: false }, async () => {
                const { filters, page_index, page_size } = this.state;
                await this.props.onDataUpdate(filters, page_index, page_size, id, false);
            });
        } else if (!this.state.order_desc) {
            this.setState({ order_desc: true }, async () => {
                const { filters, page_index, page_size } = this.state;
                await this.props.onDataUpdate(filters, page_index, page_size, id, true);
            });
        } else {
            this.setState({ order_by: "" as ColumnId<TableRow>, order_desc: false }, async () => {
                const { filters, page_index, page_size } = this.state;
                await this.props.onDataUpdate(filters, page_index, page_size, "" as ColumnId<TableRow>, false);
            });
        }
    };

    handleFilterChange = (id: keyof TableRow, value: string, delay: number = 0) => {
        const new_filters = { ...this.state.filters, [id]: value };
        this.setState({ filters: new_filters, page_index: 0 }, () => {
            clearTimeout(this.filter_timeout);
            this.filter_timeout = setTimeout(async () => {
                const { filters, page_size, order_by, order_desc } = this.state;
                await this.props.onDataUpdate(filters, 0, page_size, order_by, order_desc);
            }, delay);
        });
        if (this.props.onFiltersChange !== undefined) {
            this.props.onFiltersChange(new_filters);
        }
    };

    handleSettingsOpen = () => {
        this.setState({ show_settings: true });
    };

    handleSettingsClose = () => {
        this.setState({ show_settings: false });
    };

    handleExportConfigOpen = () => {
        this.setState({ show_export_config: true });
    };

    handleExportConfigClose = () => {
        this.setState({ show_export_config: false });
    };

    handleUpdateTotalCount = async () => {
        if (this.props.onTotalCountUpdate !== undefined) {
            const { filters } = this.state;
            await this.props.onTotalCountUpdate(filters);
        }
    };

    handleExport = async (export_all: boolean): Promise<boolean> => {
        const { data, file_name, title } = await this.getExportData(export_all);
        if (data === null) {
            return false;
        }

        const wb = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, worksheet, title);
        XLSX.writeFile(wb, `${file_name}-${new Date().toISOString().slice(0, 19).replace(/\s|:/g, "-")}.xlsx`);
        return true;
    };

    render() {
        const {
            error,
            columns,
            rows,
            footer,
            class_name,
            total_count,
            id,
            is_loading,
            is_updating,
            page_sizes,
            show_details,
            always_visible_columns,
            onScroll,
            onDetailsToggle,
        } = this.props;
        const { filters, order_by, order_desc, page_index, page_size } = this.state;
        const hidden_columns = new Set(loadHiddenColumns<TableRow>(id));
        return (
            <div className={classNames("large-data-table", class_name)}>
                <div className="large-data-table__main" onScroll={onScroll}>
                    {is_loading === true ? (
                        <Loader class_name="large-data-table__loader" />
                    ) : error !== "" ? (
                        <ErrorComponent className="large-data-table__alert" type="error" msg={error} />
                    ) : (
                        <>
                            <table
                                className={classNames("large-data-table__table table", {
                                    "is-updating": is_updating,
                                })}
                            >
                                <thead>
                                    <tr className="large-data-table__table-row">
                                        {columns.map(column => {
                                            if (hidden_columns.has(column.id) && !show_details) {
                                                return null;
                                            }

                                            return (
                                                <th
                                                    key={column.id}
                                                    className="large-data-table__table-header"
                                                    data-id={column.id}
                                                >
                                                    <HeaderCell
                                                        column={column}
                                                        filters={filters}
                                                        page_index={page_index}
                                                        page_size={page_size}
                                                        order_by={order_by}
                                                        order_desc={order_desc}
                                                        table_props={this.props}
                                                        clearFilters={this.clearFilters}
                                                        onFilterChange={this.handleFilterChange}
                                                        onSortChange={this.handleSortChange}
                                                    />
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr key={i} className="large-data-table__table-row">
                                            {columns.map(column => {
                                                if (hidden_columns.has(column.id) && !show_details) {
                                                    return null;
                                                }

                                                return (
                                                    <td
                                                        key={column.id}
                                                        className="large-data-table__table-data"
                                                        data-id={column.id}
                                                    >
                                                        <DataCell column={column} row={row} table_props={this.props} />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                                {footer !== undefined && rows.length !== 0 && (
                                    <tfoot>
                                        <tr className="large-data-table__table-row">
                                            {columns.map(column => {
                                                if (hidden_columns.has(column.id) && !show_details) {
                                                    return null;
                                                }

                                                return (
                                                    <th
                                                        key={column.id}
                                                        className="large-data-table__table-footer"
                                                        data-id={column.id}
                                                    >
                                                        <FooterCell
                                                            column={column}
                                                            footer={footer}
                                                            table_props={this.props}
                                                            filters={filters}
                                                            page_index={page_index}
                                                            page_size={page_size}
                                                            order_by={order_by}
                                                            order_desc={order_desc}
                                                        />
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                            {rows.length === 0 && (
                                <ErrorComponent
                                    className="large-data-table__alert"
                                    type="no-data"
                                    msg={translate("common.no_data", "No data")}
                                />
                            )}
                        </>
                    )}
                </div>
                <StatusBar
                    total_count={total_count}
                    is_loading={is_loading}
                    is_updating={is_updating}
                    page_sizes={page_sizes}
                    page_index={page_index}
                    page_size={page_size}
                    show_details={show_details}
                    gotoPage={this.gotoPage}
                    gotoNextPage={this.gotoNextPage}
                    gotoPreviousPage={this.gotoPreviousPage}
                    setPageSize={this.setPageSize}
                    onDetailsToggle={onDetailsToggle}
                    onSettingsOpen={this.handleSettingsOpen}
                    onExportConfigOpen={this.handleExportConfigOpen}
                />
                <ExportConfigModal
                    columns={columns}
                    show={this.state.show_export_config}
                    rows={rows}
                    total_count={total_count}
                    always_visible_columns={always_visible_columns}
                    onClose={this.handleExportConfigClose}
                    onExport={this.handleExport}
                    onTotalCountUpdate={this.handleUpdateTotalCount}
                />
                <SettingsModal
                    columns={columns}
                    show={this.state.show_settings}
                    table_id={id}
                    always_visible_columns={always_visible_columns}
                    onClose={this.handleSettingsClose}
                />
            </div>
        );
    }
}

export default LargeDataTable;
